import React, { useEffect, useMemo, useState } from "react";
import { API, Auth, graphqlOperation } from "aws-amplify";
import { listPlayerParents, listPlayers, listRosterMemberships, listTeamPlayers, listUserAssignments, listUsers } from "../graphql/queries";
import PlayerProfileModal from "../components/PlayerProfileModal";
import { buildCoachTeamIds, buildPlayerTeamIds, type RosterMembershipLike, type UserAssignmentLike } from "../lib/relationshipScope";
import type { ActiveRoleScope } from "../lib/roleContext";

interface PitchLogsPageProps {
  userRoles: string[];
  activeRoleScope?: ActiveRoleScope | null;
}

interface Player {
  id: string;
  name: string;
  teamId: string;
}

interface Team {
  id: string;
  name: string;
  organizationId?: string | null;
  leagueId?: string | null;
  ageGroup?: string | null;
  coachUsernames?: string[] | null;
}

interface TeamPlayerLink {
  id: string;
  teamID: string;
  playerID: string;
}

interface PlayerParentLink {
  id: string;
  playerId: string;
  parentEmail: string;
}

interface AppUser {
  id: string;
  givenName?: string | null;
  familyName?: string | null;
  roles?: string[] | null;
}

interface PitchLog {
  id: string;
  playerId: string;
  teamId: string;
  playerRosterTeamId?: string | null;
  date: string;
  pitches: number;
  outsRecorded?: number | null;
  isSubstitute?: boolean | null;
  enteredByUsername?: string | null;
  lastModifiedByUsername?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

interface PitchLogAuditEntry {
  id: string;
  pitchLogId: string;
  action: string;
  changedByUsername: string;
  changedAt: string;
  summary?: string | null;
  beforeSnapshot?: string | null;
  afterSnapshot?: string | null;
}

interface RosterMembership extends RosterMembershipLike {}

interface UserAssignment extends UserAssignmentLike {}

const LIST_TEAMS_FOR_PITCHING = /* GraphQL */ `
  query ListTeamsForPitching($limit: Int, $nextToken: String) {
    listTeams(limit: $limit, nextToken: $nextToken) {
      items {
        id
        name
        organizationId
        leagueId
        ageGroup
        coachUsernames
      }
      nextToken
    }
  }
`;

const LIST_PITCH_LOGS_AUDIT = /* GraphQL */ `
  query ListPitchLogsAudit($limit: Int, $nextToken: String) {
    listPitchLogs(limit: $limit, nextToken: $nextToken) {
      items {
        id
        playerId
        teamId
        playerRosterTeamId
        date
        pitches
        outsRecorded
        isSubstitute
        enteredByUsername
        lastModifiedByUsername
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

const CREATE_PITCH_LOG_AUDIT = /* GraphQL */ `
  mutation CreatePitchLogAudit($input: CreatePitchLogInput!) {
    createPitchLog(input: $input) {
      id
      playerId
      teamId
      playerRosterTeamId
      date
      pitches
      outsRecorded
      isSubstitute
      enteredByUsername
      lastModifiedByUsername
      createdAt
      updatedAt
    }
  }
`;

const UPDATE_PITCH_LOG_AUDIT = /* GraphQL */ `
  mutation UpdatePitchLogAudit($input: UpdatePitchLogInput!) {
    updatePitchLog(input: $input) {
      id
      playerId
      teamId
      playerRosterTeamId
      date
      pitches
      outsRecorded
      isSubstitute
      enteredByUsername
      lastModifiedByUsername
      createdAt
      updatedAt
    }
  }
`;

const DELETE_PITCH_LOG_AUDIT = /* GraphQL */ `
  mutation DeletePitchLogAudit($input: DeletePitchLogInput!) {
    deletePitchLog(input: $input) {
      id
    }
  }
`;

const LIST_PITCH_LOG_AUDIT_ENTRIES = /* GraphQL */ `
  query ListPitchLogAuditEntries($limit: Int, $nextToken: String) {
    listPitchLogAuditEntries(limit: $limit, nextToken: $nextToken) {
      items {
        id
        pitchLogId
        action
        changedByUsername
        changedAt
        summary
        beforeSnapshot
        afterSnapshot
      }
      nextToken
    }
  }
`;

const CREATE_PITCH_LOG_AUDIT_ENTRY = /* GraphQL */ `
  mutation CreatePitchLogAuditEntry($input: CreatePitchLogAuditEntryInput!) {
    createPitchLogAuditEntry(input: $input) {
      id
    }
  }
`;

const today = () => new Date().toISOString().slice(0, 10);
const EXTERNAL_TEAM_OPTION_ID = "__external_non_platform_team__";
const EXTERNAL_TEAM_PREFIX = "external:";

const isExternalTeamId = (value?: string | null): boolean =>
  Boolean(value && value.startsWith(EXTERNAL_TEAM_PREFIX));

const externalTeamNameFromId = (value?: string | null): string => {
  if (!isExternalTeamId(value)) {
    return "";
  }
  return (value as string).slice(EXTERNAL_TEAM_PREFIX.length).trim();
};

const parseAge = (value?: string | null): number | null => {
  if (!value) {
    return null;
  }
  const match = value.toUpperCase().match(/(\d{1,2})\s*U/);
  if (!match) {
    return null;
  }
  const num = Number(match[1]);
  return Number.isFinite(num) ? num : null;
};

const teamAge = (team?: Team): number | null => {
  if (!team) {
    return null;
  }
  return parseAge(team.ageGroup) ?? parseAge(team.name);
};

const prettyDateTime = (value?: string | null): string => {
  if (!value) {
    return "Unknown";
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
};

const formatValue = (value: unknown): string => {
  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }
  if (value == null || value === "") {
    return "-";
  }
  return String(value);
};

const parseSnapshot = (value?: string | null): Record<string, unknown> => {
  if (!value) {
    return {};
  }
  try {
    return JSON.parse(value);
  } catch {
    return {};
  }
};

const AUDIT_FIELD_LABELS: Record<string, string> = {
  date: "Date",
  teamId: "Game Team",
  playerId: "Player",
  pitches: "Pitches",
  outsRecorded: "Outs",
  isSubstitute: "Substitute",
  playerRosterTeamId: "Roster Team",
  enteredByUsername: "Entered By",
  lastModifiedByUsername: "Last Modified By"
};

const AUDIT_FIELD_ORDER = [
  "date",
  "teamId",
  "playerId",
  "pitches",
  "outsRecorded",
  "isSubstitute",
  "playerRosterTeamId",
  "enteredByUsername",
  "lastModifiedByUsername"
];

const AUDIT_IGNORED_KEYS = new Set(["id", "__typename", "createdAt", "updatedAt"]);

const diffSnapshotFields = (beforeState: Record<string, unknown>, afterState: Record<string, unknown>) => {
  const orderedKeys: string[] = [];
  for (const key of AUDIT_FIELD_ORDER) {
    orderedKeys.push(key);
  }
  for (const key of [...Object.keys(beforeState), ...Object.keys(afterState)]) {
    if (!orderedKeys.includes(key)) {
      orderedKeys.push(key);
    }
  }
  const diffs: Array<{ key: string; label: string; beforeValue: unknown; afterValue: unknown }> = [];
  for (const key of orderedKeys) {
    if (AUDIT_IGNORED_KEYS.has(key)) {
      continue;
    }
    const beforeValue = beforeState[key];
    const afterValue = afterState[key];
    if (beforeValue !== afterValue) {
      diffs.push({
        key,
        label: AUDIT_FIELD_LABELS[key] ?? key,
        beforeValue,
        afterValue
      });
    }
  }
  return diffs;
};

const PitchLogsPage: React.FC<PitchLogsPageProps> = ({ userRoles, activeRoleScope }) => {
  const [players, setPlayers] = useState<Player[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamPlayers, setTeamPlayers] = useState<TeamPlayerLink[]>([]);
  const [rosterMemberships, setRosterMemberships] = useState<RosterMembership[]>([]);
  const [userAssignments, setUserAssignments] = useState<UserAssignment[]>([]);
  const [logs, setLogs] = useState<PitchLog[]>([]);
  const [auditEntries, setAuditEntries] = useState<PitchLogAuditEntry[]>([]);
  const [appUsers, setAppUsers] = useState<AppUser[]>([]);
  const [playerParents, setPlayerParents] = useState<PlayerParentLink[]>([]);

  const [username, setUsername] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [editingLogId, setEditingLogId] = useState<string | null>(null);

  const [playerId, setPlayerId] = useState("");
  const [teamId, setTeamId] = useState("");
  const [externalTeamName, setExternalTeamName] = useState("");
  const [date, setDate] = useState(today());
  const [pitches, setPitches] = useState("");
  const [outsRecorded, setOutsRecorded] = useState("");
  const [isSubstitute, setIsSubstitute] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [sortKey, setSortKey] = useState<"date" | "player" | "team" | "outs">("date");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [expandedLogs, setExpandedLogs] = useState<Record<string, boolean>>({});
  const [activePlayerModalId, setActivePlayerModalId] = useState<string | null>(null);

  const isPrivileged = userRoles.includes("Commish") || userRoles.includes("SuperAdmin");
  const hasCoachRole = userRoles.includes("Coach");
  const hasParentRole = userRoles.includes("Parent") || userRoles.includes("Parents");
  const isSuperAdmin = userRoles.includes("SuperAdmin");
  const activeAsParent = activeRoleScope?.targetType === "parent";
  const activeTeamScopeId = activeRoleScope?.targetType === "team" ? activeRoleScope.teamId ?? "" : "";

  const playerById = useMemo(() => {
    const out: Record<string, Player> = {};
    for (const player of players) {
      out[player.id] = player;
    }
    return out;
  }, [players]);

  const teamById = useMemo(() => {
    const out: Record<string, Team> = {};
    for (const team of teams) {
      out[team.id] = team;
    }
    return out;
  }, [teams]);

  const getTeamDisplayName = (rawTeamId?: string | null): string => {
    if (!rawTeamId) {
      return "Unknown team";
    }
    if (isExternalTeamId(rawTeamId)) {
      const externalName = externalTeamNameFromId(rawTeamId);
      return externalName ? `External: ${externalName}` : "External team";
    }
    return teamById[rawTeamId]?.name ?? "Unknown team";
  };

  const coachedTeamIds = useMemo(() => {
    if (!hasCoachRole || isPrivileged || !username) {
      return new Set<string>();
    }
    return buildCoachTeamIds({ username, teams, userAssignments });
  }, [hasCoachRole, isPrivileged, teams, userAssignments, username]);

  const playerTeamIds = useMemo(
    () => buildPlayerTeamIds({ players, teamPlayers, rosterMemberships }),
    [players, rosterMemberships, teamPlayers]
  );

  const activeScopedTeamIds = useMemo(() => {
    const out = new Set<string>();
    if (activeRoleScope?.targetType === "team" && activeRoleScope.teamId) {
      out.add(activeRoleScope.teamId);
    }
    if (activeRoleScope?.targetType === "organization" && activeRoleScope.organizationId) {
      teams
        .filter((team) => team.organizationId === activeRoleScope.organizationId)
        .forEach((team) => out.add(team.id));
    }
    if (activeRoleScope?.targetType === "league" && activeRoleScope.leagueId) {
      teams.filter((team) => team.leagueId === activeRoleScope.leagueId).forEach((team) => out.add(team.id));
    }
    return out;
  }, [activeRoleScope, teams]);

  const visiblePlayerIdsForCoach = useMemo(() => {
    if (!hasCoachRole || isPrivileged) {
      return new Set<string>();
    }
    const out = new Set<string>();
    for (const player of players) {
      const membership = playerTeamIds[player.id] ?? new Set<string>();
      if ([...membership].some((id) => coachedTeamIds.has(id))) {
        out.add(player.id);
      }
    }
    return out;
  }, [coachedTeamIds, hasCoachRole, isPrivileged, playerTeamIds, players]);

  const visiblePlayerIdsForParent = useMemo(() => {
    if ((!hasParentRole && !activeAsParent) || (isPrivileged && !activeAsParent) || !userEmail.trim()) {
      return new Set<string>();
    }
    const normalizedEmail = userEmail.trim().toLowerCase();
    return new Set<string>(
      playerParents
        .filter((link) => (link.parentEmail ?? "").trim().toLowerCase() === normalizedEmail)
        .map((link) => link.playerId)
    );
  }, [activeAsParent, hasParentRole, isPrivileged, playerParents, userEmail]);

  const visiblePlayerIds = useMemo(() => {
    if (activeAsParent) {
      return visiblePlayerIdsForParent;
    }
    if (activeScopedTeamIds.size > 0) {
      const out = new Set<string>();
      for (const player of players) {
        const membership = playerTeamIds[player.id] ?? new Set<string>();
        if ([...membership].some((id) => activeScopedTeamIds.has(id))) {
          out.add(player.id);
        }
      }
      return out;
    }
    if (isPrivileged || (!hasCoachRole && !hasParentRole)) {
      return new Set<string>(players.map((player) => player.id));
    }
    const out = new Set<string>();
    for (const id of visiblePlayerIdsForCoach) {
      out.add(id);
    }
    for (const id of visiblePlayerIdsForParent) {
      out.add(id);
    }
    return out;
  }, [activeAsParent, activeScopedTeamIds, hasCoachRole, hasParentRole, isPrivileged, playerTeamIds, players, visiblePlayerIdsForCoach, visiblePlayerIdsForParent]);

  const substituteEligiblePlayers = useMemo(() => {
    if (!hasCoachRole || isPrivileged || !isSubstitute || !teamId) {
      return [] as Player[];
    }
    const selectedTeam = teamById[teamId];
    if (!selectedTeam) {
      return [];
    }
    const selectedOrg = selectedTeam.organizationId ?? "";
    const selectedAge = teamAge(selectedTeam);

    return players.filter((player) => {
      const primaryTeam = teamById[player.teamId];
      const primaryAge = teamAge(primaryTeam);
      const memberships = playerTeamIds[player.id] ?? new Set<string>();
      const sameOrg = [...memberships].some((memberTeamId) => teamById[memberTeamId]?.organizationId === selectedOrg);
      if (!sameOrg) {
        return false;
      }
      if (selectedAge == null || primaryAge == null) {
        return true;
      }
      return primaryAge === selectedAge || primaryAge === selectedAge - 1;
    });
  }, [hasCoachRole, isPrivileged, isSubstitute, playerTeamIds, players, teamById, teamId]);

  const playerOptions = useMemo(() => {
    if (activeAsParent) {
      return players.filter((player) => visiblePlayerIdsForParent.has(player.id));
    }
    if (activeScopedTeamIds.size > 0 && !isSubstitute) {
      return players.filter((player) =>
        [...(playerTeamIds[player.id] ?? new Set<string>())].some((id) => activeScopedTeamIds.has(id))
      );
    }
    if (isPrivileged || (!hasCoachRole && !hasParentRole)) {
      return players;
    }
    if (isSubstitute && hasCoachRole && !isPrivileged) {
      return substituteEligiblePlayers;
    }
    return players.filter((player) => visiblePlayerIds.has(player.id));
  }, [activeAsParent, activeScopedTeamIds, hasCoachRole, hasParentRole, isPrivileged, isSubstitute, playerTeamIds, players, substituteEligiblePlayers, visiblePlayerIds, visiblePlayerIdsForParent]);

  const teamOptions = useMemo(() => {
    if (activeAsParent) {
      return [{ id: EXTERNAL_TEAM_OPTION_ID, name: "External / Non-platform team" } as Team];
    }
    if (activeTeamScopeId && !isSubstitute) {
      return teams.filter((team) => team.id === activeTeamScopeId);
    }
    if (activeScopedTeamIds.size > 0 && !isSubstitute) {
      return teams.filter((team) => activeScopedTeamIds.has(team.id));
    }
    if (isPrivileged || (!hasCoachRole && !hasParentRole)) {
      return teams;
    }
    if (hasParentRole && !hasCoachRole) {
      return [{ id: EXTERNAL_TEAM_OPTION_ID, name: "External / Non-platform team" } as Team];
    }
    if (isSubstitute && hasCoachRole && !isPrivileged) {
      return teams.filter((team) => coachedTeamIds.has(team.id));
    }
    if (!playerId) {
      if (hasCoachRole && !hasParentRole) {
        return teams.filter((team) => coachedTeamIds.has(team.id));
      }
      const mixedRoleOptions = teams.filter((team) =>
        players.some((player) => visiblePlayerIds.has(player.id) && (playerTeamIds[player.id] ?? new Set<string>()).has(team.id))
      );
      return [...mixedRoleOptions, { id: EXTERNAL_TEAM_OPTION_ID, name: "External / Non-platform team" } as Team];
    }
    const memberships = playerTeamIds[playerId] ?? new Set<string>();
    if (hasCoachRole && !hasParentRole) {
      return teams.filter((team) => coachedTeamIds.has(team.id) && memberships.has(team.id));
    }
    const mixedRoleOptions = teams.filter((team) => memberships.has(team.id));
    return [...mixedRoleOptions, { id: EXTERNAL_TEAM_OPTION_ID, name: "External / Non-platform team" } as Team];
  }, [activeAsParent, activeScopedTeamIds, activeTeamScopeId, coachedTeamIds, hasCoachRole, hasParentRole, isPrivileged, isSubstitute, playerId, playerTeamIds, players, teams, visiblePlayerIds]);

  const scopedLogs = useMemo(() => {
    if (activeAsParent) {
      return logs.filter((log) => visiblePlayerIdsForParent.has(log.playerId));
    }
    if (activeScopedTeamIds.size > 0) {
      return logs.filter((log) => {
        if (activeScopedTeamIds.has(log.teamId) || (log.playerRosterTeamId && activeScopedTeamIds.has(log.playerRosterTeamId))) {
          return true;
        }
        return [...(playerTeamIds[log.playerId] ?? new Set<string>())].some((id) => activeScopedTeamIds.has(id));
      });
    }
    if (isPrivileged || (!hasCoachRole && !hasParentRole)) {
      return logs;
    }
    return logs.filter((log) => visiblePlayerIds.has(log.playerId));
  }, [activeAsParent, activeScopedTeamIds, hasCoachRole, hasParentRole, isPrivileged, logs, playerTeamIds, visiblePlayerIds, visiblePlayerIdsForParent]);

  const sortedLogs = useMemo(() => {
    return [...scopedLogs].sort((a, b) => {
      let compare = 0;
      if (sortKey === "player") {
        compare = (playerById[a.playerId]?.name ?? "").localeCompare(playerById[b.playerId]?.name ?? "");
      } else if (sortKey === "team") {
        compare = getTeamDisplayName(a.teamId).localeCompare(getTeamDisplayName(b.teamId));
      } else if (sortKey === "outs") {
        compare = (a.outsRecorded ?? -1) - (b.outsRecorded ?? -1);
      } else {
        compare = a.date.localeCompare(b.date);
      }
      return sortDirection === "asc" ? compare : -compare;
    });
  }, [getTeamDisplayName, playerById, scopedLogs, sortDirection, sortKey]);

  const auditByLogId = useMemo(() => {
    const out: Record<string, PitchLogAuditEntry[]> = {};
    for (const entry of auditEntries) {
      if (!out[entry.pitchLogId]) {
        out[entry.pitchLogId] = [];
      }
      out[entry.pitchLogId].push(entry);
    }
    for (const key of Object.keys(out)) {
      out[key].sort((a, b) => (b.changedAt ?? "").localeCompare(a.changedAt ?? ""));
    }
    return out;
  }, [auditEntries]);

  const appUserById = useMemo(() => {
    const out: Record<string, AppUser> = {};
    for (const user of appUsers) {
      out[user.id] = user;
    }
    return out;
  }, [appUsers]);

  const summarizeChanges = (beforeState: Partial<PitchLog>, afterState: Partial<PitchLog>): string => {
    const fields: Array<{ key: keyof PitchLog; label: string }> = [
      { key: "date", label: "Date" },
      { key: "teamId", label: "Team" },
      { key: "playerId", label: "Player" },
      { key: "pitches", label: "Pitches" },
      { key: "outsRecorded", label: "Outs" },
      { key: "isSubstitute", label: "Substitute" }
    ];
    const changed: string[] = [];
    for (const field of fields) {
      const beforeValue = (beforeState as any)[field.key];
      const afterValue = (afterState as any)[field.key];
      if (beforeValue !== afterValue) {
        changed.push(`${field.label}: ${formatValue(beforeValue)} -> ${formatValue(afterValue)}`);
      }
    }
    return changed.length > 0 ? changed.join(" | ") : "No field-level changes recorded.";
  };

  const writeAuditEntry = async (args: {
    action: "UPDATE";
    pitchLogId: string;
    beforeState?: Partial<PitchLog>;
    afterState: Partial<PitchLog>;
  }) => {
    const { action, pitchLogId, beforeState, afterState } = args;
    await API.graphql(
      graphqlOperation(CREATE_PITCH_LOG_AUDIT_ENTRY, {
        input: {
          pitchLogId,
          action,
          changedByUsername: username || "unknown",
          changedAt: new Date().toISOString(),
          summary: summarizeChanges(beforeState ?? {}, afterState),
          beforeSnapshot: JSON.stringify(beforeState ?? {}),
          afterSnapshot: JSON.stringify(afterState ?? {})
        }
      })
    );
  };

  const loadData = async () => {
    setErrorMessage("");
    try {
      const authUser = await Auth.currentAuthenticatedUser();
      setUsername(authUser?.username ?? "");
      setUserEmail((authUser?.attributes?.email ?? "").toLowerCase());

      const [playersResult, teamsResult, teamPlayersResult, rosterMembershipsResult, userAssignmentsResult, logsResult, auditResult, playerParentsResult] = await Promise.all([
        API.graphql(graphqlOperation(listPlayers, { limit: 1000 })),
        API.graphql(graphqlOperation(LIST_TEAMS_FOR_PITCHING, { limit: 1000 })),
        API.graphql(graphqlOperation(listTeamPlayers, { limit: 4000 })),
        API.graphql(graphqlOperation(listRosterMemberships, { limit: 5000 })),
        API.graphql(graphqlOperation(listUserAssignments, { limit: 5000 })),
        API.graphql(graphqlOperation(LIST_PITCH_LOGS_AUDIT, { limit: 4000 })),
        API.graphql(graphqlOperation(LIST_PITCH_LOG_AUDIT_ENTRIES, { limit: 6000 })),
        API.graphql(graphqlOperation(listPlayerParents, { limit: 3000 }))
      ]);

      let loadedAppUsers: AppUser[] = [];
      try {
        const usersResult = await API.graphql(graphqlOperation(listUsers, { limit: 3000 }));
        loadedAppUsers = (((usersResult as any)?.data?.listUsers?.items ?? []).filter(Boolean) as AppUser[]);
      } catch {
        loadedAppUsers = [];
      }

      const loadedPlayers = ((playersResult as any)?.data?.listPlayers?.items ?? []).filter(Boolean);
      const loadedTeams = ((teamsResult as any)?.data?.listTeams?.items ?? []).filter(Boolean);
      const loadedTeamPlayers = ((teamPlayersResult as any)?.data?.listTeamPlayers?.items ?? []).filter(Boolean);
      const loadedLogs = ((logsResult as any)?.data?.listPitchLogs?.items ?? []).filter(Boolean);
      const loadedPlayerParents = ((playerParentsResult as any)?.data?.listPlayerParents?.items ?? []).filter(Boolean);
      const loadedAuditEntries = (((auditResult as any)?.data?.listPitchLogAuditEntries?.items ?? []).filter(Boolean) as PitchLogAuditEntry[])
        .filter((entry) => entry.action === "UPDATE");

      loadedLogs.sort((a: PitchLog, b: PitchLog) => {
        if (a.date === b.date) {
          return (b.updatedAt ?? "").localeCompare(a.updatedAt ?? "");
        }
        return b.date.localeCompare(a.date);
      });

      setPlayers(loadedPlayers);
      setTeams(loadedTeams);
      setTeamPlayers(loadedTeamPlayers);
      setRosterMemberships(((rosterMembershipsResult as any)?.data?.listRosterMemberships?.items ?? []).filter(Boolean));
      setUserAssignments(((userAssignmentsResult as any)?.data?.listUserAssignments?.items ?? []).filter(Boolean));
      setLogs(loadedLogs);
      setAuditEntries(loadedAuditEntries);
      setAppUsers(loadedAppUsers);
      setPlayerParents(loadedPlayerParents);
    } catch (error) {
      setErrorMessage(`Failed to load pitch logs: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  };

  const resetForm = () => {
    setEditingLogId(null);
    setPlayerId("");
    setTeamId("");
    setExternalTeamName("");
    setDate(today());
    setPitches("");
    setOutsRecorded("");
    setIsSubstitute(false);
  };

  const canCoachUseSelected = (selectedPlayer: Player, selectedTeamId: string): boolean => {
    if (isExternalTeamId(selectedTeamId)) {
      return hasParentRole && visiblePlayerIdsForParent.has(selectedPlayer.id);
    }
    if (hasParentRole && !hasCoachRole && !isPrivileged) {
      return false;
    }
    if (!hasCoachRole || isPrivileged) {
      return true;
    }
    if (!coachedTeamIds.has(selectedTeamId)) {
      return false;
    }
    const memberships = playerTeamIds[selectedPlayer.id] ?? new Set<string>();
    if (!isSubstitute) {
      return memberships.has(selectedTeamId);
    }
    const selectedTeam = teamById[selectedTeamId];
    if (!selectedTeam) {
      return false;
    }
    const selectedOrg = selectedTeam.organizationId ?? "";
    const selectedAge = teamAge(selectedTeam);
    const sameOrg = [...memberships].some((id) => teamById[id]?.organizationId === selectedOrg);
    if (!sameOrg) {
      return false;
    }
    const primaryAge = teamAge(teamById[selectedPlayer.teamId]);
    if (selectedAge == null || primaryAge == null) {
      return true;
    }
    return primaryAge === selectedAge || primaryAge === selectedAge - 1;
  };

  const handleSave = async () => {
    if (!playerId || !teamId || !date || !pitches) {
      setErrorMessage("Player, team, date, and pitches are required.");
      return;
    }

    const selectedPlayer = playerById[playerId];
    if (!selectedPlayer) {
      setErrorMessage("Please select a valid player.");
      return;
    }

    const numericPitches = Number(pitches);
    const numericOuts = outsRecorded ? Number(outsRecorded) : undefined;

    if (!Number.isFinite(numericPitches) || numericPitches < 0) {
      setErrorMessage("Pitches must be a non-negative number.");
      return;
    }

    if (numericOuts !== undefined && (!Number.isFinite(numericOuts) || numericOuts < 0)) {
      setErrorMessage("Outs recorded must be a non-negative number.");
      return;
    }

    const resolvedTeamId = teamId === EXTERNAL_TEAM_OPTION_ID
      ? `${EXTERNAL_TEAM_PREFIX}${externalTeamName.trim()}`
      : teamId;

    if (teamId === EXTERNAL_TEAM_OPTION_ID && !externalTeamName.trim()) {
      setErrorMessage("Please enter a non-platform team name.");
      return;
    }

    if (!canCoachUseSelected(selectedPlayer, resolvedTeamId)) {
      setErrorMessage(hasParentRole && !hasCoachRole && !isPrivileged
        ? "Parent accounts can only log pitches for players linked to your email and only for external/non-platform teams."
        : "Coach permissions allow only your team, and substitute players must be same age or one age-group down in the same organization.");
      return;
    }

    setErrorMessage("");

    try {
      if (editingLogId) {
        const existingLog = logs.find((log) => log.id === editingLogId);
        await API.graphql(
          graphqlOperation(UPDATE_PITCH_LOG_AUDIT, {
            input: {
              id: editingLogId,
              playerId,
              teamId: resolvedTeamId,
              playerRosterTeamId: selectedPlayer.teamId,
              date,
              pitches: numericPitches,
              outsRecorded: numericOuts,
              isSubstitute,
              lastModifiedByUsername: username
            }
          })
        );
        await writeAuditEntry({
          action: "UPDATE",
          pitchLogId: editingLogId,
          beforeState: existingLog ?? {},
          afterState: {
            playerId,
            teamId: resolvedTeamId,
            playerRosterTeamId: selectedPlayer.teamId,
            date,
            pitches: numericPitches,
            outsRecorded: numericOuts,
            isSubstitute
          }
        });
      } else {
        await API.graphql(
          graphqlOperation(CREATE_PITCH_LOG_AUDIT, {
            input: {
              playerId,
              teamId: resolvedTeamId,
              playerRosterTeamId: selectedPlayer.teamId,
              date,
              pitches: numericPitches,
              outsRecorded: numericOuts,
              isSubstitute,
              enteredByUsername: username,
              lastModifiedByUsername: username
            }
          })
        );
      }

      resetForm();
      await loadData();
    } catch (error) {
      setErrorMessage(`Failed to save pitch log: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  };

  const handleDeleteLog = async (log: PitchLog) => {
    if (!isSuperAdmin) {
      return;
    }
    if (!window.confirm("Delete this pitch log? This cannot be undone.")) {
      return;
    }
    try {
      setErrorMessage("");
      await API.graphql(graphqlOperation(DELETE_PITCH_LOG_AUDIT, { input: { id: log.id } }));
      setExpandedLogs((prev) => {
        const next = { ...prev };
        delete next[log.id];
        return next;
      });
      await loadData();
    } catch (error) {
      setErrorMessage(`Failed to delete pitch log: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  };

  const canEditLog = (log: PitchLog): boolean => {
    if (!hasCoachRole || isPrivileged) {
      return true;
    }
    return coachedTeamIds.has(log.teamId);
  };

  const canViewAuditLog = (log: PitchLog): boolean => {
    if (isPrivileged) {
      return true;
    }
    if (!hasCoachRole) {
      return canEditLog(log);
    }
    if (coachedTeamIds.has(log.teamId)) {
      return true;
    }
    if (log.playerRosterTeamId && coachedTeamIds.has(log.playerRosterTeamId)) {
      return true;
    }
    const player = playerById[log.playerId];
    if (player?.teamId && coachedTeamIds.has(player.teamId)) {
      return true;
    }
    return false;
  };

  const formatAuditDisplayValue = (fieldKey: string, value: unknown): string => {
    if ((fieldKey === "teamId" || fieldKey === "playerRosterTeamId") && typeof value === "string" && value) {
      return getTeamDisplayName(value);
    }
    if (fieldKey === "playerId" && typeof value === "string" && value) {
      const playerName = playerById[value]?.name;
      return playerName ? playerName : value;
    }
    if (fieldKey === "isSubstitute" && typeof value === "boolean") {
      return value ? "Yes" : "No";
    }
    return formatValue(value);
  };

  const formatUserDisplay = (rawUsername?: string | null): string => {
    const usernameValue = (rawUsername ?? "").trim();
    if (!usernameValue) {
      return "Unknown";
    }
    const profile = appUserById[usernameValue];
    const fullName = `${profile?.givenName ?? ""} ${profile?.familyName ?? ""}`.trim();
    const profileRoles = profile?.roles ?? [];
    const normalizedRoles = profileRoles.map((role) => role.trim().toLowerCase());
    const relationship = normalizedRoles.includes("coach")
      ? "Coach"
      : (normalizedRoles.includes("parent") || normalizedRoles.includes("parents"))
      ? "Parent"
      : normalizedRoles.includes("commish")
      ? "Commish"
      : normalizedRoles.includes("superadmin")
      ? "SuperAdmin"
      : "";

    if (fullName && relationship) {
      return `${fullName} (${relationship})`;
    }
    if (fullName) {
      return `${fullName} (${usernameValue})`;
    }
    return usernameValue;
  };

  const startEditLog = (log: PitchLog) => {
    setEditingLogId(log.id);
    setPlayerId(log.playerId);
    if (isExternalTeamId(log.teamId)) {
      setTeamId(EXTERNAL_TEAM_OPTION_ID);
      setExternalTeamName(externalTeamNameFromId(log.teamId));
    } else {
      setTeamId(log.teamId);
      setExternalTeamName("");
    }
    setDate(log.date);
    setPitches(String(log.pitches));
    setOutsRecorded(typeof log.outsRecorded === "number" ? String(log.outsRecorded) : "");
    setIsSubstitute(Boolean(log.isSubstitute));
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!playerId || !teamOptions.some((team) => team.id === teamId)) {
      setTeamId(teamOptions[0]?.id ?? "");
    }
  }, [playerId, teamId, teamOptions]);

  return (
    <>
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-2">Pitch Logs</h1>
      <p className="text-gray-600 mb-6">Pitch logs track last modified by user, when it was modified, and the team played for.</p>

      {errorMessage && <p className="mb-4" style={{ color: "#c1121f" }}>{errorMessage}</p>}

      <div className="bubble-card p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <select value={playerId} onChange={(event) => setPlayerId(event.target.value)} className="border px-3 py-2 rounded">
            <option value="">Select player</option>
            {playerOptions.map((player) => (
              <option key={player.id} value={player.id}>
                {player.name}
              </option>
            ))}
          </select>

          <select value={teamId} onChange={(event) => setTeamId(event.target.value)} className="border px-3 py-2 rounded">
            <option value="">Team played for</option>
            {teamOptions.map((team) => (
              <option key={team.id} value={team.id}>
                {team.name}
              </option>
            ))}
          </select>

          {teamId === EXTERNAL_TEAM_OPTION_ID && (
            <input
              type="text"
              value={externalTeamName}
              onChange={(event) => setExternalTeamName(event.target.value)}
              className="border px-3 py-2 rounded"
              placeholder="External team name"
            />
          )}

          <input type="date" value={date} onChange={(event) => setDate(event.target.value)} className="border px-3 py-2 rounded" />
          <input
            type="number"
            min={0}
            value={pitches}
            onChange={(event) => setPitches(event.target.value)}
            className="border px-3 py-2 rounded"
            placeholder="Pitches"
          />
          <input
            type="number"
            min={0}
            value={outsRecorded}
            onChange={(event) => setOutsRecorded(event.target.value)}
            className="border px-3 py-2 rounded"
            placeholder="Outs"
          />
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <label className="text-sm text-gray-600">
            <input
              type="checkbox"
              checked={isSubstitute}
              onChange={(event) => setIsSubstitute(event.target.checked)}
              style={{ marginRight: 8 }}
            />
            Substitute
          </label>
          <button onClick={handleSave} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
            {editingLogId ? "Update Log" : "Save Log"}
          </button>
          {editingLogId && (
            <button onClick={resetForm} className="bg-gray-200 text-gray-800 px-4 py-2 rounded">
              Cancel
            </button>
          )}
          <button className="table-sort" onClick={() => setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"))}>
            {sortDirection === "asc" ? "Ascending" : "Descending"}
          </button>
        </div>
      </div>

      <div className="bubble-table-wrapper">
        <table className="bubble-table">
          <thead>
            <tr>
              <th><button className="table-sort" onClick={() => setSortKey("date")}>Date</button></th>
              <th><button className="table-sort" onClick={() => setSortKey("player")}>Player</button></th>
              <th><button className="table-sort" onClick={() => setSortKey("team")}>Team</button></th>
              <th>Pitches</th>
              <th><button className="table-sort" onClick={() => setSortKey("outs")}>Outs</button></th>
              <th>Sub</th>
              <th>Last Modified</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedLogs.length === 0 && (
              <tr>
                <td colSpan={8} className="text-gray-600">No pitch logs found.</td>
              </tr>
            )}
            {sortedLogs.map((log) => (
              <React.Fragment key={log.id}>
                <tr>
                  <td>{log.date}</td>
                  <td>
                    {playerById[log.playerId] ? (
                      <button className="table-sort" onClick={() => setActivePlayerModalId(log.playerId)}>
                        {playerById[log.playerId]?.name}
                      </button>
                    ) : (
                      "Unknown player"
                    )}
                  </td>
                  <td>{getTeamDisplayName(log.teamId)}</td>
                  <td>{log.pitches}</td>
                  <td>{typeof log.outsRecorded === "number" ? log.outsRecorded : "-"}</td>
                  <td>{log.isSubstitute ? "Yes" : "No"}</td>
                  <td className="text-xs text-gray-600">
                    {formatUserDisplay(log.lastModifiedByUsername ?? log.enteredByUsername)}<br />
                    {prettyDateTime(log.updatedAt)}
                  </td>
                  <td>
                    {(canViewAuditLog(log) || canEditLog(log) || isSuperAdmin) && (
                      <div className="flex gap-1">
                        {canViewAuditLog(log) && (
                          <button
                            onClick={() => setExpandedLogs((prev) => ({ ...prev, [log.id]: !prev[log.id] }))}
                            className="bg-emerald-600 text-white px-2 py-1 rounded text-xs"
                          >
                            {expandedLogs[log.id] ? "Hide Audit" : "Audit"}
                          </button>
                        )}
                        {canEditLog(log) && (
                          <button onClick={() => startEditLog(log)} className="bg-yellow-500 text-white px-2 py-1 rounded text-xs">
                            Edit
                          </button>
                        )}
                        {isSuperAdmin && (
                          <button onClick={() => handleDeleteLog(log)} className="bg-red-600 text-white px-2 py-1 rounded text-xs">
                            Delete
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
                {expandedLogs[log.id] && canViewAuditLog(log) && (
                  <tr>
                    <td colSpan={8}>
                      <div className="mt-2 bubble-table-wrapper">
                        <table className="bubble-table">
                          <thead>
                            <tr>
                              <th>When</th>
                              <th>Who</th>
                              <th>Action</th>
                              <th>Changes</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(auditByLogId[log.id] ?? []).length === 0 && (
                              <tr>
                                <td colSpan={4} className="text-gray-600">No audit entries found for this log.</td>
                              </tr>
                            )}
                            {(auditByLogId[log.id] ?? []).map((entry) => {
                              const beforeState = parseSnapshot(entry.beforeSnapshot);
                              const afterState = parseSnapshot(entry.afterSnapshot);
                              const diffs = diffSnapshotFields(beforeState, afterState);
                              return (
                                <tr key={entry.id}>
                                  <td>{prettyDateTime(entry.changedAt)}</td>
                                  <td>{formatUserDisplay(entry.changedByUsername)}</td>
                                  <td>{entry.action}</td>
                                  <td>
                                    <div className="mt-1 space-y-1">
                                      {diffs.length === 0 && (
                                        <div className="text-xs text-gray-600">No field-level changes captured.</div>
                                      )}
                                      {diffs.map((diff, index) => (
                                        <div key={`${entry.id}-${diff.key}-${index}`} className="text-xs text-gray-700">
                                          <span className="font-medium">{diff.label}:</span>{" "}
                                          <span className="text-gray-500">{formatAuditDisplayValue(diff.key, diff.beforeValue)}</span>{" "}
                                          {"->"}{" "}
                                          <span className="text-gray-900">{formatAuditDisplayValue(diff.key, diff.afterValue)}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
    {activePlayerModalId && (
      <PlayerProfileModal
        playerId={activePlayerModalId}
        userRoles={userRoles}
        onClose={() => setActivePlayerModalId(null)}
      />
    )}
    </>
  );
};

export default PitchLogsPage;
